import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { FormInputState, FormModel, group, Result, ResultValue } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import {
    validateRequire,
    validateSpecialCharactersInUrl,
    validateSpaces,
    validateStringLength,
    validateListOfNumbersWithLimit,
} from 'src/api/utils/formValidators';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';
import { LandingPageModelType } from 'src/api/config/landingPage/getLandingPage';
import { LandingPageBodyModelType } from 'src/api/config/landingPage/postLandingPage';
import { UploadImageState } from '../UploadImageState/UploadImageState';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';

const LandingPageDetailsIO = t.interface({
    idsList: t.array(t.number),
});

export type LandingPageDetailsType = t.TypeOf<typeof LandingPageDetailsIO>;

const isLandingPageDetails = createGuard(LandingPageDetailsIO);

export type LandingPageContentResponse = LandingPageImageLoading | LandingPageImageReady | LandingPageImageError;

interface LandingPageImageLoading {
    type: 'loading';
}

type LandingPageType = PromotionLandingPageType;
interface LandingPageImageReady {
    type: 'ready';
    value: Array<LandingPageType>;
}

interface LandingPageImageError {
    type: 'error';
    value: string;
}

export type PromotionLandingPageType = LandingPageModelType;
export interface LPType {
    slug: string;
    title: string;
    promoCode: string;
    imgHTMLUrl: string;
    imgUrl: string;
    dateFrom: string;
    dateTo: string;
    ctaText: string;
    ctaUrl: string;
    tcText: string;
    idsList: string;
    isActive: boolean;
    frequencyShow: string;
    bannerType: string;
    actionTemplate: string;
    isHomepage: boolean;
}

type PageType = 'edit' | 'add';

const defaultSelectsValues = {
    frequencyShow: 'once',
    actionTemplate: 'signup',
    showForm: 'show',
    bannerType: 'html',
};
export class LandingPageState {
    @observable readonly landingPagesResource: Resource<Array<LandingPageModelType>>;
    @observable page: string = 'add';
    @observable isActiveFlagChanged: boolean = false;
    @observable editedLP: PromotionLandingPageType | null = null;
    @observable isLoading: boolean = false;
    @observable errorMessage: string = '';
    @observable onGoingUploadFile: File | null = null;

    readonly slugModel: FormModel<string>;
    readonly slug: FormInputState<string>;
    readonly slugStatus: InputStatus;
    readonly titleModel: FormModel<string>;
    readonly title: FormInputState<string>;
    readonly titleStatus: InputStatus;
    readonly promoCodeModel: FormModel<string>;
    readonly promoCode: FormInputState<string>;
    readonly promoCodeStatus: InputStatus;
    readonly imgHTMLUrlModel: FormModel<string>;
    readonly imgHTMLUrl: FormInputState<string>;
    readonly imgHTMLUrlStatus: InputStatus;
    readonly imgUrlModel: FormModel<string>;
    readonly imgUrl: FormInputState<string>;
    readonly ctaTextModel: FormModel<string>;
    readonly ctaText: FormInputState<string>;
    readonly ctaTextStatus: InputStatus;
    readonly ctaUrlModel: FormModel<string>;
    readonly ctaUrl: FormInputState<string>;
    readonly ctaUrlStatus: InputStatus;
    readonly tcTextModel: FormModel<string>;
    readonly tcText: FormInputState<string>;
    readonly tcTextStatus: TextareaStatus;
    readonly idsListModel: FormModel<string>;
    readonly idsList: FormInputState<string>;
    readonly idsListStatus: TextareaStatus;
    readonly isActiveModel: FormModel<boolean>;
    readonly isActive: FormInputState<boolean>;
    readonly isActiveStatus: InputStatus;
    readonly isHomepageModel: FormModel<boolean>;
    readonly isHomepage: FormInputState<boolean>;
    readonly isHomepageStatus: InputStatus;
    readonly dateFromModel: FormModel<string>;
    readonly dateFrom: FormInputState<string>;
    readonly dateFromStatus: InputStatus;
    readonly dateToModel: FormModel<string>;
    readonly dateTo: FormInputState<string>;
    readonly dateToStatus: InputStatus;
    readonly frequencyShowModel: FormModel<string>;
    readonly frequencyShow: FormInputState<string>;
    readonly frequencyShowStatus: InputStatus;
    readonly actionTemplateModel: FormModel<string>;
    readonly actionTemplate: FormInputState<string>;
    readonly actionTemplateStatus: InputStatus;
    readonly bannerModel: FormModel<string>;
    readonly bannerType: FormInputState<string>;
    readonly bannerStatus: InputStatus;
    readonly form: FormModel<LPType>;

    public uploadImageState: UploadImageState;

    constructor() {
        this.uploadImageState = new UploadImageState('landing-page');
        this.landingPagesResource = new Resource(async (): Promise<Array<LandingPageModelType>> => {
            this.isLoading = true;
            const response = await apiCommon.getLandingPage.run({});
            this.isLoading = false;
            return response;
        });

        this.slug = new FormInputState('', false);
        this.slugModel = this.slug.map(validateRequire).map(validateSpecialCharactersInUrl).map(validateSpaces);
        this.slugStatus = new InputStatus(this.slugModel);

        this.promoCode = new FormInputState('', false);
        this.promoCodeModel = this.promoCode.map(validateRequire);
        this.promoCodeStatus = new InputStatus(this.promoCodeModel);

        this.title = new FormInputState('', false);
        this.titleModel = this.title.map(validateRequire).map(validateStringLength);
        this.titleStatus = new InputStatus(this.titleModel);

        this.imgHTMLUrl = new FormInputState('', false);
        this.imgHTMLUrlModel = this.imgHTMLUrl.toModel();
        this.imgHTMLUrlStatus = new InputStatus(this.imgHTMLUrlModel);

        this.imgUrl = new FormInputState('', false);
        this.imgUrlModel = this.imgUrl.toModel();

        this.ctaText = new FormInputState('', false);
        this.ctaTextModel = this.ctaText.map(validateRequire).map(validateStringLength);
        this.ctaTextStatus = new InputStatus(this.ctaTextModel);

        this.ctaUrl = new FormInputState('', false);
        this.ctaUrlModel = this.ctaUrl.toModel();
        this.ctaUrlStatus = new InputStatus(this.ctaUrlModel);

        this.frequencyShow = new FormInputState(defaultSelectsValues.frequencyShow, false);
        this.frequencyShowModel = this.frequencyShow.map(validateRequire);
        this.frequencyShowStatus = new InputStatus(this.frequencyShowModel);

        this.actionTemplate = new FormInputState(defaultSelectsValues.actionTemplate, false);
        this.actionTemplateModel = this.actionTemplate.map(validateRequire);
        this.actionTemplateStatus = new InputStatus(this.actionTemplateModel);

        this.bannerType = new FormInputState(defaultSelectsValues.bannerType, false);
        this.bannerModel = this.bannerType.toModel();
        this.bannerStatus = new InputStatus(this.bannerModel);

        this.tcText = new FormInputState('', false);
        this.tcTextModel = this.tcText.toModel();
        this.tcTextStatus = new TextareaStatus(this.tcTextModel);

        this.idsList = new FormInputState('', false);
        this.idsListModel = this.idsList.map(validateListOfNumbersWithLimit(1000));
        this.idsListStatus = new TextareaStatus(this.idsListModel);

        this.dateFrom = new FormInputState('', false);
        this.dateFromModel = this.dateFrom.map(validateRequire);
        this.dateFromStatus = new InputStatus(this.dateFromModel);

        this.dateTo = new FormInputState('', false);
        this.dateToModel = this.dateTo.map(validateRequire);
        this.dateToStatus = new InputStatus(this.dateToModel);

        this.isActive = new FormInputState(false, false);
        this.isActiveModel = this.isActive.toModel();
        this.isActiveStatus = new InputStatus(this.isActiveModel);

        this.isHomepage = new FormInputState(false, false);
        this.isHomepageModel = this.isHomepage.toModel();
        this.isHomepageStatus = new InputStatus(this.isHomepageModel);

        this.form = group({
            slug: this.slugModel,
            promoCode: this.promoCodeModel,
            title: this.titleModel,
            imgHTMLUrl: this.imgHTMLUrlModel,
            imgUrl: this.imgUrlModel,
            ctaText: this.ctaTextModel,
            ctaUrl: this.ctaUrlModel,
            tcText: this.tcTextModel,
            idsList: this.idsListModel,
            isActive: this.isActiveModel,
            dateFrom: this.dateFromModel,
            dateTo: this.dateToModel,
            frequencyShow: this.frequencyShowModel,
            bannerType: this.bannerModel,
            actionTemplate: this.actionTemplateModel,
            isHomepage: this.isHomepageModel,
        }).map((item: LPType): Result<LPType> => {
            return new ResultValue({
                slug: item.slug,
                title: item.title,
                promoCode: item.promoCode,
                imgHTMLUrl: item.imgHTMLUrl,
                imgUrl: item.imgUrl,
                dateFrom: item.dateFrom,
                dateTo: item.dateTo,
                ctaText: item.ctaText,
                ctaUrl: item.ctaUrl,
                tcText: item.tcText,
                idsList: item.idsList,
                isActive: item.isActive,
                frequencyShow: item.frequencyShow,
                bannerType: item.bannerType,
                actionTemplate: item.actionTemplate,
                isHomepage: item.isHomepage,
            });
        });
        makeAutoObservable(this);
    }

    @action handleCancel = (): void => {
        if (this.isEditMode && this.editedLP !== null) {
            this.setEditedLP(this.editedLP);
        } else {
            this.form.reset();
        }
    };

    @action setDefaultState = (): void => {
        this.setPage('add');
        this.editedLP = null;
        this.form.reset();
    };

    @action setPage = (value: PageType): void => {
        this.page = value;
    };

    @computed get homepageActiveLP(): PromotionLandingPageType | null {
        if (this.landingPages.type === 'ready') {
            return this.landingPages.value.find((LP) => LP.is_homepage === true && LP.is_active === true) ?? null;
        }
        return null;
    }

    @action setActiveFlag = async (LP: PromotionLandingPageType): Promise<void> => {
        this.isActiveFlagChanged = true;
        this.isLoading = true;

        if (LP.is_homepage && this.homepageActiveLP !== null && LP.slug !== this.homepageActiveLP.slug) {
            const currentHomepage: LandingPageBodyModelType = {
                ...this.homepageActiveLP,
                is_active: false,
            };
            await this.submitEditLP(currentHomepage, this.homepageActiveLP.id);
        }

        const payload: LandingPageBodyModelType = {
            ...LP,
            is_active: !LP.is_active,
        };

        const submitResult = await this.submitEditLP(payload, LP.id);

        if (submitResult) {
            await this.landingPagesResource.refresh();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
        this.isActiveFlagChanged = false;
        this.isLoading = false;
    };

    @computed get isDirectLinkSelected(): boolean {
        return this.actionTemplate.value === 'direct-link';
    }

    @computed get isHTMLBannerType(): boolean {
        return this.bannerType.value === 'html';
    }

    @action onRemove = async (id: number): Promise<void> => {
        const removeResult = await apiCommon.deleteLandingPage.run({ id });

        if (removeResult) {
            await this.landingPagesResource.refresh();

            this.form.reset();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    @computed get prepareDetails(): LandingPageDetailsType {
        const tempDetails: LandingPageDetailsType = {
            idsList: [],
        };
        const idsList = this.idsListModel.result;
        if (idsList instanceof ResultValue && idsList.value !== '') {
            const idsListParsed = idsList.value
                .split(',')
                .map((elem) => Number(elem))
                .filter((elem) => !isNaN(elem));
            tempDetails.idsList = idsListParsed;
        }

        return tempDetails;
    }

    @action handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;
        const newDateHash = Number(new Date()).toString(36);

        if (result instanceof ResultValue) {
            const payload: LandingPageBodyModelType = {
                slug: result.value.isHomepage && !this.isEditMode ? `homepage-${newDateHash}` : result.value.slug,
                title: result.value.title,
                promo_code: result.value.promoCode,
                img_html_url: result.value.imgHTMLUrl,
                img_url: this.uploadImageState.imgUrl,
                date_from: result.value.dateFrom,
                date_to: result.value.dateTo,
                cta_text: result.value.ctaText,
                cta_url: result.value.ctaUrl,
                tc_text: result.value.tcText,
                is_active: result.value.isActive,
                banner_type: result.value.bannerType,
                show_frequency: result.value.frequencyShow,
                action_template: result.value.actionTemplate,
                is_homepage: result.value.isHomepage,
                details: this.prepareDetails,
            };

            const submitResult =
                this.isEditMode && this.editedLP !== null
                    ? await this.submitEditLP(payload, this.editedLP.id)
                    : await this.submitCreateLP(payload);

            if (submitResult) {
                await this.landingPagesResource.refresh();
                this.setDefaultState();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }

        this.isLoading = false;
    };

    @action submitCreateLP = async (data: LandingPageBodyModelType): Promise<number | Error | null> => {
        let response: number | Error | null;
        try {
            response = await apiCommon.postLandingPage.run({ data });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action submitEditLP = async (data: LandingPageBodyModelType, id: number): Promise<number | Error | null> => {
        let response: number | Error | null;
        try {
            response = await apiCommon.putLandingPage.run({ data, id });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @computed get homepageLPsQuantity(): number | null {
        if (this.landingPages.type === 'ready') {
            const homepageLPs = this.landingPages.value.filter(
                (LP: PromotionLandingPageType | null) => LP?.is_homepage === true
            );
            return homepageLPs.length;
        }
        return null;
    }

    @action setEditedLP = (LP: PromotionLandingPageType) => {
        this.editedLP = LP;
        if (this.landingPages.type === 'ready') {
            if (LP !== null && LP !== undefined) {
                this.uploadImageState.handleChooseImage(LP.img_url ?? '');
                this.isHomepage.setValue(LP.is_homepage);
                this.slug.setValue(LP.slug);
                this.title.setValue(LP.title);
                this.promoCode.setValue(LP.promo_code);
                this.imgUrl.setValue(LP.img_url ?? '');
                this.imgHTMLUrl.setValue(LP.img_html_url ?? '');
                this.dateFrom.setValue(LP.date_from);
                this.dateTo.setValue(LP.date_to);
                this.ctaText.setValue(LP.cta_text);
                this.ctaUrl.setValue(LP.cta_url);
                this.tcText.setValue(LP.tc_text);
                this.isActive.setValue(LP.is_active);
                this.actionTemplate.setValue(LP.action_template);
                this.bannerType.setValue(LP.banner_type);
                this.frequencyShow.setValue(LP.show_frequency);

                if (isLandingPageDetails(LP.details)) {
                    this.idsList.setValue(LP.details.idsList.toString());
                }
            }
        }
    };

    @computed get landingPages(): LandingPageContentResponse {
        const landingPage = this.landingPagesResource.get();
        if (landingPage.type === 'ready' && landingPage.value !== null) {
            return {
                type: 'ready',
                value: landingPage.value,
            };
        }
        if (landingPage.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching landing page info',
        };
    }

    @computed get landingPagesSorted(): Array<LandingPageType> | null {
        if (this.landingPages.type === 'ready') {
            return this.landingPages.value.sort((lp1: LandingPageType, lp2: LandingPageType) =>
                lp1 !== null && lp2 !== null ? lp1.title.localeCompare(lp2.title) : -1
            );
        }
        return null;
    }

    @computed get isFormValid(): boolean {
        return this.form.result instanceof ResultValue;
    }

    @computed get isEditMode(): boolean {
        return this.page === 'edit' || this.isActiveFlagChanged;
    }

    @computed get selectedImg(): string | null {
        return this.imgUrl.value === '' ? null : this.imgUrl.value;
    }

    @computed get addedIds(): Array<string> {
        if (this.idsListModel.result instanceof ResultValue) {
            return this.idsListModel.result.value.split(',').filter((elem) => elem !== '');
        }

        return [];
    }

    static createForContext(): LandingPageState {
        return new LandingPageState();
    }
}
